import React, { PureComponent } from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import { pipe, values, isEmpty, filter, length, lt, identity } from 'ramda';

import {
  Input,
  SubmitButton,
  LoginLink,
  BottomText,
  Form,
  Description,
  Checkbox,
  CheckboxWrapper,
} from './registerForm.styles';
import messages from './registerForm.messages';
import { Modal } from '../modal';
import { REGISTRATION_FIELDS as FIELDS } from '../../../modules/auth/auth.constants';
import { ignoreSpace } from '../../utils/input';

const messagesMap = {
  [FIELDS.firstName]: messages.firstNamePlaceholder,
  [FIELDS.lastName]: messages.lastNamePlaceholder,
  [FIELDS.agreements1]: messages.agreements1Placeholder,
  [FIELDS.agreements2]: messages.agreements2Placeholder,
};

export class RegisterForm extends PureComponent {
  static propTypes = {
    errors: PropTypes.object,
    handleSubmit: PropTypes.func,
    intl: PropTypes.object,
    isSubmitting: PropTypes.bool,
    showTerms: PropTypes.func.isRequired,
    hideModal: PropTypes.func.isRequired,
    values: PropTypes.object,
    onClose: PropTypes.func,
    onLoginClick: PropTypes.func,
  };

  static defaultProps = {
    onClose: identity,
    onLoginClick: identity,
  };

  get isEmpty() {
    return pipe(
      values,
      filter(isEmpty),
      length,
      lt(0)
    )(this.props.values);
  }

  handleSubmit = async e => {
    e.preventDefault();
    this.props.handleSubmit(e);
  };

  handleClose = () => this.props.onClose();

  handleLoginClick = () => this.props.onLoginClick();

  handleTermsClick = e => {
    e.stopPropagation();
    this.props.showTerms();
  };

  renderTextField = ({ field, form }) => (
    <Input
      {...field}
      touched={form.touched[field.name]}
      error={form.errors[field.name]}
      placeholder={this.props.intl.formatMessage(messagesMap[field.name])}
    />
  );

  renderConsentCheckbox = ({ field, form }) => (
    <Checkbox {...field} error={form.errors[field.name]} touched={form.touched[field.name]}>
      <FormattedHTMLMessage {...messagesMap[field.name]} />
    </Checkbox>
  );

  renderTermsCheckbox = ({ field, form }) => {
    const termsLink = (
      <a onClick={this.handleTermsClick}>
        <FormattedMessage {...messages.termsLinkLabel} />
      </a>
    );

    return (
      <Checkbox {...field} error={form.errors[field.name]} touched={form.touched[field.name]}>
        <FormattedMessage
          {...messagesMap[field.name]}
          values={{
            termsLink,
          }}
        />
      </Checkbox>
    );
  };

  renderEmailField = ({ field, form }) => (
    <Input
      {...field}
      touched={form.touched[field.name]}
      error={form.errors[field.name]}
      type="email"
      onKeyDown={ignoreSpace}
      placeholder={this.props.intl.formatMessage(messages.emailPlaceholder)}
    />
  );

  renderPasswordField = ({ field, form }) => (
    <Input
      {...field}
      touched={form.touched[field.name]}
      error={form.errors[field.name]}
      type="password"
      placeholder={this.props.intl.formatMessage(messages.passwordPlaceholder)}
    />
  );

  render() {
    return (
      <Modal onClose={this.handleClose} title={this.props.intl.formatMessage(messages.title)}>
        <Description>
          <FormattedMessage {...messages.description} />
        </Description>

        <Form onSubmit={this.handleSubmit}>
          <Field name={FIELDS.firstName} render={this.renderTextField} />
          <Field name={FIELDS.lastName} render={this.renderTextField} />
          <Field name={FIELDS.email} render={this.renderEmailField} />
          <Field name={FIELDS.password} render={this.renderPasswordField} />

          <CheckboxWrapper>
            <Field name={FIELDS.agreements1} render={this.renderConsentCheckbox} />
            <Field name={FIELDS.agreements2} render={this.renderTermsCheckbox} />
          </CheckboxWrapper>

          <SubmitButton
            type="submit"
            disabled={this.isEmpty || this.props.isSubmitting}
            label={this.props.intl.formatMessage(messages.submitButton)}
          />
        </Form>

        <BottomText>
          <FormattedMessage {...messages.haveAnAccount} />
          <LoginLink onClick={this.handleLoginClick}>
            <FormattedMessage {...messages.login} />
          </LoginLink>
        </BottomText>
      </Modal>
    );
  }
}
